import React, { useState, useMemo, useEffect, useCallback, type FC } from 'react';
import DropdownMenu, { DropdownItemGroup, type OnOpenChangeArgs } from '@atlaskit/dropdown-menu';
import { Box, xcss } from '@atlaskit/primitives';
import {
	ForgeAppsContainer,
	useForgeApps,
	useIsAnyAppLoading,
} from '@atlassian/jira-forge-action-apps-controller/src/index.tsx';
import { ErrorState } from '@atlassian/jira-issue-navigator-actions-common/src/ui/error/index.tsx';
import { FireUiAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { usePluggableItemsQuery } from '../services/query/index.tsx';
import {
	useInlineDialogZIndexOverrideLayoutEffect,
	useModalDialogCleanupEffect,
	useRegisterModalDialogAnalytics,
} from '../common/dialog-controls.tsx';
import { ACTIONS_MIN_WIDTH, CONTAINER_MIN_WIDTH } from './constants.tsx';
import { DropdownItemWithApps as DropdownItem } from './dropdown-item/index.tsx';
import { Empty } from './empty/index.tsx';
import { PluggableItemsFilterButton as FilterButton } from './filter-button/index.tsx';
import { IssueNavigatorActionRuntimeContainer } from './forge-runtime-container/index.tsx';
import { isInOrIsIFrame } from './utils.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PluggableItems: FC<Record<any, any>> = () => {
	const [fetchData, { loading, error, data }] = usePluggableItemsQuery();
	const [isAnyAppLoading] = useIsAnyAppLoading();
	const [, { closeApps }] = useForgeApps();
	const totalCount = data?.length ?? 0;
	const [isOpen, setIsOpen] = useState(false);
	const allIds: string[] = useMemo(() => data?.map((item) => item?.id) ?? [], [data]);
	const registerModalDialogAnalytics = useRegisterModalDialogAnalytics();
	useModalDialogCleanupEffect();
	useInlineDialogZIndexOverrideLayoutEffect();

	// Close the dropdown when all Forge apps are loaded.
	// In practice it will be a single app at a time, but we can't rely on that.
	useEffect(() => {
		if (!isAnyAppLoading) {
			setIsOpen(false);
		}
	}, [isAnyAppLoading]);

	// Close all Forge apps when the data is re-fetched
	useEffect(() => {
		(async () => {
			await closeApps();
		})();
	}, [data, closeApps]);

	const onOpenChange = useCallback(
		(attrs: OnOpenChangeArgs) => {
			// Ignore events originating from a matching iframe or an element inside an iframe
			const eventTarget = attrs.event?.target;

			if (
				eventTarget &&
				eventTarget instanceof HTMLElement &&
				isInOrIsIFrame(eventTarget, allIds)
			) {
				return;
			}

			if (attrs.isOpen) {
				if (!loading) {
					fetchData(error);
				}

				// Only setup analytics when the dialog is opened
				registerModalDialogAnalytics();
			}

			setIsOpen(attrs.isOpen);
		},
		[allIds, error, fetchData, loading, registerModalDialogAnalytics],
	);

	return (
		<>
			<DropdownMenu
				isLoading={loading}
				testId="issue-navigator-action-pluggable-items.ui.filter-button"
				trigger={({ triggerRef, ...props }) => <FilterButton {...props} ref={triggerRef} />}
				shouldFlip
				placement="bottom-end"
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				spacing="compact"
				shouldRenderToParent
			>
				<Box xcss={containerStyles}>
					{!error && totalCount > 0 ? (
						<DropdownItemGroup>
							{data?.map((item) => (
								<DropdownItem key={item.id} item={item} totalCount={totalCount} />
							))}
						</DropdownItemGroup>
					) : (
						<Box xcss={maxWidthContainerStyles} role="menuitem">
							{error ? (
								<>
									<ErrorState pageAction="apps" onRetry={() => fetchData(true)} />
									<FireUiAnalytics
										action="viewed"
										actionSubject="errorDropdown"
										actionSubjectId="issueNavigatorActionsPluggableItems"
									/>
								</>
							) : (
								<Empty />
							)}
						</Box>
					)}
				</Box>
			</DropdownMenu>
			<IssueNavigatorActionRuntimeContainer />
		</>
	);
};

const PluggableItemsWithForge = () => (
	<ForgeAppsContainer scope="issue-navigator-action">
		<PluggableItems />
	</ForgeAppsContainer>
);

export default PluggableItemsWithForge;

const containerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${ACTIONS_MIN_WIDTH}px`,
});

const maxWidthContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${CONTAINER_MIN_WIDTH}px`,
});
